import {
  Megaphone,
  Search,
  Instagram,
  MessageCircle,
  Mail,
  Route,
  Globe,
  Clapperboard,
} from "lucide-react";

export const SERVICE_PAGES = [
  {
    id: "meta-ads",
    slug: "meta-ads",
    path: "/services/meta-ads",
    icon: Megaphone,
    title: "Meta Ads",
    desc: "Generate targeted enquiries through Facebook and Instagram campaigns, built around the audiences, offers and seasons that matter to Indian Hajj & Umrah agencies.",
    includes: ["Audience strategy", "Campaign setup", "Creative direction", "Lead generation", "Retargeting", "Campaign optimization"],
    cta: "Explore Meta Ads",
    seoTitle: "Meta Ads for Hajj & Umrah Agencies in India | Nexus Catalyst",
    seoDesc: "Facebook and Instagram advertising for Hajj and Umrah travel agencies in India: audience strategy, lead generation, creative direction and retargeting.",
    h1: "Meta Ads for Hajj & Umrah Agencies in India",
    heroTag: "Meta Ads",
    heroCopy: "Facebook and Instagram are where many Indian families first discover and evaluate Hajj and Umrah packages. We build Meta campaigns that turn that attention into qualified enquiries, not just likes.",
    points: [
      {
        heading: "Audience strategy built around pilgrimage intent",
        body: [
          "Targeting is planned around the markets you actually serve, whether that is Maharashtra, Gujarat, Telangana, Kerala, Uttar Pradesh, West Bengal, Karnataka, Tamil Nadu or specific cities like Mumbai, Surat, Ahmedabad, Hyderabad, Delhi and Lucknow.",
          "Where you have genuine customer lists or past enquiry data, we can build lookalike audiences from them. Where you do not, we start with structured interest and behavior testing and let performance data refine the audience.",
        ],
      },
      {
        heading: "Lead generation, not just engagement",
        body: [
          "Campaigns are built to produce enquiries: instant forms with qualifying questions, landing pages with clear package communication, or direct click-to-WhatsApp paths depending on how your team sells best.",
          "Creative direction focuses on package clarity, preparation guidance and agency credibility, because trust is the real conversion lever in pilgrimage travel.",
        ],
      },
      {
        heading: "Retargeting that respects the decision cycle",
        body: [
          "Hajj and Umrah decisions often take weeks and involve the whole family. Retargeting keeps your agency present through that period with useful, respectful messaging instead of pressure.",
          "Campaigns are reviewed continuously, and budgets shift toward the audiences and creatives that produce real conversations.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can Facebook and Instagram ads generate Hajj leads in India?",
        a: "Yes. Meta campaigns with package-led creative, careful geographic targeting and a fast follow-up path can produce a steady flow of enquiries. Results depend on your offer, budget, season and sales process, so we optimize toward cost per qualified enquiry rather than clicks.",
      },
      {
        q: "Should we use instant forms or a landing page?",
        a: "Instant forms usually produce higher volume at lower friction. Landing pages usually produce better-qualified enquiries. We typically test both and let the data decide for your market.",
      },
      {
        q: "How much should we spend on Facebook ads?",
        a: "Start with a test budget your agency is comfortable with, measure cost per qualified enquiry, then scale what works. Advertising spend is paid to Meta directly and is separate from Nexus Catalyst's service fees.",
      },
    ],
    relatedArticles: ["meta-ads-hajj-umrah-agencies", "hajj-lead-generation-india", "hajj-umrah-advertising-strategy-india"],
  },
  {
    id: "google-ads",
    slug: "google-ads",
    path: "/services/google-ads",
    icon: Search,
    title: "Google Ads",
    desc: "Capture high-intent Indian customers actively searching for Hajj and Umrah travel options, with geographic targeting kept flexible around the cities and states you serve.",
    includes: ["Search campaign strategy", "High-intent keyword targeting", "Flexible geographic targeting", "Landing-page recommendations", "Conversion tracking", "Ongoing optimization"],
    cta: "Explore Google Ads",
    extra: "keywords",
    keywords: [
      "Hajj packages",
      "Hajj packages from India",
      "Umrah packages",
      "Umrah packages from Mumbai",
      "Umrah packages from Surat",
      "Umrah packages from Ahmedabad",
      "Umrah packages from Hyderabad",
      "Hajj travel agency",
      "Umrah travel agency",
      "Hajj packages near me",
    ],
    seoTitle: "Google Ads for Hajj & Umrah Agencies in India | Nexus Catalyst",
    seoDesc: "Google Ads for Hajj and Umrah travel agencies in India: high-intent search campaigns, city-level targeting and conversion-focused landing pages.",
    h1: "Google Ads for Hajj & Umrah Agencies in India",
    heroTag: "Google Ads",
    heroCopy: "When someone in India searches for a Hajj or Umrah package, they are already close to a decision. Google Ads puts your agency in front of that intent at the exact moment it appears.",
    points: [
      {
        heading: "Capture Indian customers who are already searching",
        body: [
          "Search campaigns target high-intent queries such as package searches, city-specific searches and agency searches. Keyword themes are validated with real research before budgets are recommended, never assumed.",
          "Campaigns are structured so each theme has its own messaging and landing path, which keeps quality high and wasted spend low.",
        ],
      },
      {
        heading: "Geographic targeting, kept flexible",
        body: [
          "Whether you serve one metro or many states, targeting can be set at city, region or national level and adjusted as performance data comes in.",
          "This matters in a market where a Surat-based agency and a Hyderabad-based agency compete for very different customers.",
        ],
      },
      {
        heading: "Landing pages and measurement",
        body: [
          "Clicks only become enquiries on the page after the ad. We recommend landing-page improvements and set up conversion tracking so every rupee is accountable to a real enquiry, call or WhatsApp conversation.",
        ],
      },
    ],
    faqs: [
      {
        q: "Which keywords should a Hajj agency target on Google?",
        a: "Focus on high-intent themes: package queries, city-specific package queries, price queries and agency queries. Exact keyword lists should come from live search data for your markets, which we research before launch.",
      },
      {
        q: "Are Google Ads expensive for Umrah agencies?",
        a: "Competitive queries cost more per click, but intent is also higher. The right measure is cost per qualified enquiry, not cost per click. We structure campaigns to protect that number.",
      },
      {
        q: "Can we target only the cities we serve?",
        a: "Yes. Campaigns can run in a single city, a set of cities, selected states or across India, and can be adjusted at any time.",
      },
    ],
    relatedArticles: ["google-ads-hajj-umrah-agencies", "umrah-lead-generation-india", "hajj-umrah-marketing-costs-india"],
  },
  {
    id: "social-media",
    slug: "social-media-marketing",
    path: "/services/social-media-marketing",
    icon: Instagram,
    title: "Social Media Marketing",
    desc: "Build a professional digital presence that increases trust before the first enquiry, because prospective pilgrims and families may investigate your agency long before they make contact.",
    includes: ["Package communication", "Educational content", "Travel information", "FAQs", "Customer testimonials when genuine", "Destination content", "Pilgrimage preparation", "Agency credibility", "Seasonal campaigns"],
    cta: "Explore Social Media",
    note: "Only genuine testimonials and reviews. Never fabricated proof.",
    seoTitle: "Social Media Marketing for Hajj & Umrah Agencies in India | Nexus Catalyst",
    seoDesc: "Social media marketing for Hajj and Umrah travel agencies in India: content that builds trust before the first WhatsApp message.",
    h1: "Social Media Marketing for Hajj & Umrah Agencies",
    heroTag: "Social Media Marketing",
    heroCopy: "Build trust before the first WhatsApp message. Prospective pilgrims and families often review an agency's Instagram or Facebook presence before they ever make contact.",
    points: [
      {
        heading: "Content that communicates credibility",
        body: [
          "A strong presence covers package clarity, travel information, destination guidance, pilgrimage preparation, FAQs and genuine customer experiences, presented with consistent, professional design.",
          "The goal is simple: when a family compares three agencies online, yours should look like the one that has its act together.",
        ],
      },
      {
        heading: "Seasonal and campaign-aware calendars",
        body: [
          "Content calendars follow the pilgrimage calendar. Ramadan, Hajj season and school-holiday Umrah windows each get their own themes and offers, planned in advance rather than posted in a rush.",
        ],
      },
      {
        heading: "A presence that supports your ads",
        body: [
          "Paid campaigns perform better when the profile behind them looks active and trustworthy. Social content and advertising are planned together so each strengthens the other.",
        ],
      },
    ],
    faqs: [
      {
        q: "Which platforms matter most for Hajj and Umrah agencies?",
        a: "Instagram and Facebook carry most of the discovery and comparison behavior, while WhatsApp carries the conversation. We focus effort where your customers actually evaluate agencies.",
      },
      {
        q: "Do you create the content?",
        a: "Content production depends on the selected service scope. Creative direction and campaign creative are part of most engagements, and ongoing content calendars can be included where the scope calls for it.",
      },
      {
        q: "Can we use customer photos and testimonials?",
        a: "Yes, when they are genuine and you have permission. We never use fabricated reviews or stock imagery presented as real customers.",
      },
    ],
    relatedArticles: ["social-media-trust-hajj-umrah", "hajj-umrah-advertising-strategy-india", "convert-enquiries-into-bookings"],
  },
  {
    id: "whatsapp-marketing",
    slug: "whatsapp-marketing",
    path: "/services/whatsapp-marketing",
    icon: MessageCircle,
    title: "WhatsApp Marketing",
    desc: "Turn enquiries into conversations. For many Indian customers, WhatsApp is where the real buying discussion happens, so we structure it like part of your sales system.",
    includes: ["WhatsApp enquiry flows", "Lead follow-up", "Customer segmentation", "Response structure", "Follow-up messaging", "Re-engagement"],
    cta: "Explore WhatsApp Marketing",
    note: "We build the conversation system. We don't promise automatic bookings.",
    seoTitle: "WhatsApp Marketing for Hajj & Umrah Agencies in India | Nexus Catalyst",
    seoDesc: "WhatsApp marketing for Hajj and Umrah travel agencies in India: enquiry flows, follow-up structure, segmentation and re-engagement.",
    h1: "WhatsApp Marketing for Hajj & Umrah Agencies in India",
    heroTag: "WhatsApp Marketing",
    heroCopy: "Turn interest into real conversations. For many Indian Hajj and Umrah customers, the first serious discussion with an agency happens on WhatsApp, not on a website form.",
    points: [
      {
        heading: "Enquiry flows designed around how customers behave",
        body: [
          "Campaigns can send prospects straight into WhatsApp with a pre-filled message, so your team receives context instead of a blank chat.",
          "From there, a short qualification structure (travel dates, group size, departure city, budget range) helps your team focus on serious prospects quickly.",
        ],
      },
      {
        heading: "Follow-up that does not let enquiries go cold",
        body: [
          "Most agencies lose bookings in the follow-up, not the ad. We design follow-up sequences, response templates and re-engagement messages so every enquiry gets a consistent, timely experience.",
          "Segmentation keeps communication relevant: new enquiries, undecided prospects and past customers each receive different messages.",
        ],
      },
      {
        heading: "Consent and good practice",
        body: [
          "WhatsApp communication should go to people who enquired or opted in, with content they would reasonably expect. Legitimate marketing respects applicable consent requirements and platform policies.",
        ],
      },
    ],
    faqs: [
      {
        q: "How can WhatsApp help an Umrah travel agency?",
        a: "It shortens the path from interest to conversation. Enquiries arrive as chats your team can answer immediately, qualification happens in minutes, and follow-up lands in the channel customers check most.",
      },
      {
        q: "Can WhatsApp follow-up be automated?",
        a: "Greetings, qualifying questions and routing can be structured or partially automated. The sales conversation itself should stay human. We build the system around that balance.",
      },
      {
        q: "Is it acceptable to message past enquiries?",
        a: "Re-engagement should go to people who enquired or opted in, with relevant and respectful messages. We design re-engagement around consent and usefulness.",
      },
    ],
    relatedArticles: ["whatsapp-marketing-hajj-umrah-agencies", "convert-enquiries-into-bookings", "umrah-lead-generation-india"],
  },
  {
    id: "email-marketing",
    slug: "email-marketing",
    path: "/services/email-marketing",
    icon: Mail,
    title: "Email Marketing",
    desc: "Not every enquiry is ready to book today. Email keeps your agency present and useful while families compare, discuss and decide.",
    includes: ["Lead nurturing", "Package updates", "Educational sequences", "FAQ campaigns", "Reminder campaigns", "Re-engagement", "Seasonal communications"],
    cta: "Explore Email Marketing",
    extra: "emailFlow",
    emailFlow: ["New enquiry", "Welcome", "Package information", "Trust-building content", "FAQ", "Consultation invitation", "Reminder", "Re-engagement"],
    note: "Legitimate marketing communications should respect applicable consent requirements.",
    seoTitle: "Email Marketing for Hajj & Umrah Agencies in India | Nexus Catalyst",
    seoDesc: "Email marketing for Hajj and Umrah travel agencies in India: nurture sequences, package updates, FAQ campaigns and re-engagement.",
    h1: "Email Marketing for Hajj & Umrah Agencies in India",
    heroTag: "Email Marketing",
    heroCopy: "Not every enquiry is ready to book today. Email nurture improves the probability that legitimate enquiries receive timely, useful communication while they make up their minds.",
    points: [
      {
        heading: "A sequence, not a scatter",
        body: [
          "New enquiries enter a considered sequence: a welcome, package information, trust-building content, answers to common questions, a consultation invitation, reminders and, eventually, respectful re-engagement.",
          "Each message has one job and one clear next step.",
        ],
      },
      {
        heading: "Seasonal communication that arrives on time",
        body: [
          "Hajj announcement periods, Ramadan Umrah demand and school-holiday windows are planned into the calendar, so your list hears from you when intent is naturally rising.",
        ],
      },
      {
        heading: "Consent-first sending",
        body: [
          "Email should go to people who enquired or subscribed, with honest subject lines and easy opt-out. This protects both your deliverability and your reputation.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does email marketing work for Hajj and Umrah agencies?",
        a: "Email works best as a supporting channel. WhatsApp carries the fast conversation, while email carries package details, documentation guidance and longer nurture content that families revisit.",
      },
      {
        q: "What should an Umrah agency send by email?",
        a: "Package breakdowns, preparation guides, answers to common questions, departure updates and consultation invitations. Useful beats frequent.",
      },
      {
        q: "How often should we email our list?",
        a: "Often enough to stay present, never so often that messages become noise. The right cadence depends on your season and list, and is defined in your strategy.",
      },
    ],
    relatedArticles: ["umrah-lead-generation-india", "convert-enquiries-into-bookings", "hajj-umrah-marketing-costs-india"],
  },
  {
    id: "campaign-strategy",
    slug: "campaign-strategy",
    path: "/services/campaign-strategy",
    icon: Route,
    title: "Hajj & Umrah Campaign Strategy",
    desc: "An integrated acquisition strategy that connects every moving part, so advertising, landing pages, WhatsApp and follow-up work as one system instead of disconnected efforts.",
    includes: ["Audience", "Offer", "Creative", "Seasonality", "Acquisition channels", "Landing pages", "WhatsApp", "Follow-up", "Retargeting", "Sales process"],
    cta: "Explore Our Strategy",
    seoTitle: "Hajj & Umrah Campaign Strategy for Indian Travel Agencies | Nexus Catalyst",
    seoDesc: "Integrated Hajj and Umrah campaign strategy for Indian travel agencies: audience, offer, creative, seasonality, channels, WhatsApp and follow-up.",
    h1: "Hajj & Umrah Campaign Strategy for Indian Travel Agencies",
    heroTag: "Campaign Strategy",
    heroCopy: "Most agencies do not have an ads problem. They have a system problem: disconnected campaigns, weak follow-up and no plan for the season. Strategy fixes the order of operations.",
    points: [
      {
        heading: "Everything connected, on purpose",
        body: [
          "A campaign strategy defines your audience, offer, creative direction, seasonality plan, acquisition channels, landing pages, WhatsApp flow, follow-up structure, retargeting and the handover to your sales process.",
          "Each part is chosen because of how it supports the others, which is what separates a system from a collection of marketing activities.",
        ],
      },
      {
        heading: "Built around your market reality",
        body: [
          "A Surat Umrah specialist and a multi-city Hajj operator need different strategies. We plan around your packages, your cities, your capacity and your sales team.",
        ],
      },
      {
        heading: "From diagnosis to scale",
        body: [
          "Engagements follow our six-step process: diagnose, strategize, build, launch, optimize and scale. You always know which step you are in and what happens next.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do we need every channel at once?",
        a: "No. Most agencies start with one or two channels done properly, then expand. The strategy defines the sequence so budget goes to the highest-impact work first.",
      },
      {
        q: "How is strategy priced?",
        a: "Strategy is included within our monthly service plans, which start at ₹20,000 per month. Scope depends on channels, markets and campaign complexity. See our pricing page for starting points.",
      },
      {
        q: "Who implements the strategy?",
        a: "We do, within the agreed scope: campaigns, creative direction, lead flows and optimization. Your team handles the sales conversations, supported by the follow-up structure we design together.",
      },
    ],
    relatedArticles: ["hajj-umrah-advertising-strategy-india", "hajj-lead-generation-india", "hajj-umrah-marketing-costs-india"],
  },
  {
    id: "website-development",
    slug: "website-development",
    path: "/services/website-development",
    icon: Globe,
    title: "Website Development",
    desc: "A professional, mobile-responsive website structured to build trust and turn visitors and ad clicks into enquiries: packages, testimonials, WhatsApp and enquiry forms working together.",
    includes: ["Professional homepage", "About page", "Hajj & Umrah pages", "Packages section", "Testimonials section", "FAQ section", "Enquiry form", "WhatsApp CTA", "Mobile responsive design", "SEO foundations", "Analytics setup"],
    cta: "Explore Website Development",
    seoTitle: "Website Development for Hajj & Umrah Travel Agencies in India | Nexus Catalyst",
    seoDesc: "Professional, mobile-responsive websites for Hajj and Umrah travel agencies in India: package pages, enquiry forms, WhatsApp integration, SEO foundations and analytics.",
    h1: "Website Development for Hajj & Umrah Travel Agencies in India",
    heroTag: "Website Development",
    heroCopy: "Your website is where advertising, social media and referrals all eventually land. We build fast, mobile-responsive websites for Hajj and Umrah agencies that present packages clearly and make enquiring effortless.",
    points: [
      {
        heading: "Built to convert, not just to look good",
        body: [
          "Every page is structured around one job: moving a visitor toward an enquiry. Clear package presentation, trust-building sections, WhatsApp access and enquiry forms are placed where visitors actually make decisions.",
          "Mobile comes first, because most of your customers will see your website on a phone.",
        ],
      },
      {
        heading: "Structured for Hajj and Umrah packages",
        body: [
          "Dedicated Hajj and Umrah sections, package pages and campaign-ready landing structures mean your offers are easy to understand, compare and enquire about.",
          "Advanced builds include conversion tracking, Meta Pixel integration and a paid-traffic-ready structure, so the website is prepared before a single rupee of ad spend goes live.",
        ],
      },
      {
        heading: "One-time pricing, clear scope",
        body: [
          "Three levels: Basic at ₹20,000, Professional at ₹40,000 and Advanced at ₹50,000, each one-time. You know exactly what is included before work begins. See the pricing page for the full breakdown.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much does a travel agency website cost?",
        a: "Our one-time website packages are Basic at ₹20,000, Professional at ₹40,000 and Advanced at ₹50,000. Each level has a clearly listed scope on the pricing page, so there are no surprise add-ons mid-project.",
      },
      {
        q: "How long does a website project take?",
        a: "Timelines depend on the level you choose and how quickly package details, photos and information are provided. We confirm a timeline in writing before work begins.",
      },
      {
        q: "Will the website work with our advertising?",
        a: "Yes. Every build includes WhatsApp CTAs and enquiry forms. Professional adds stronger conversion structure and analytics configuration, and Advanced is paid-traffic-ready with conversion tracking and Meta Pixel integration.",
      },
    ],
    relatedArticles: ["hajj-umrah-marketing-costs-india", "convert-enquiries-into-bookings", "hajj-umrah-advertising-strategy-india"],
  },
  {
    id: "on-ground-content-production",
    slug: "on-ground-content-production",
    path: "/services/on-ground-content-production",
    icon: Clapperboard,
    title: "On-Ground Content Production",
    desc: "One professional content production day that gives your agency 10 polished reels ready for Instagram, Facebook and Meta Ads, so your marketing always has real, credible footage.",
    includes: ["10 reels", "Content planning", "Production direction", "Editing & delivery", "Instagram-ready formats", "Facebook-ready formats", "Meta Ads-ready formats", "Package promotion content", "Trust-building content", "Lead generation content"],
    cta: "Explore Content Production",
    seoTitle: "On-Ground Content Production for Hajj & Umrah Agencies | Nexus Catalyst",
    seoDesc: "A professional content production day for Hajj and Umrah travel agencies in India: 10 reels for Instagram, Facebook, Meta Ads, brand awareness, package promotion and lead generation.",
    h1: "On-Ground Content Production for Hajj & Umrah Agencies",
    heroTag: "Content Production",
    heroCopy: "Advertising and social media perform better with real footage. One planned production day gives your agency 10 professional reels built for Instagram, Facebook and Meta advertising.",
    points: [
      {
        heading: "One day, ten reels",
        body: [
          "We plan the shot list with you in advance, capture the content on the production day and deliver 10 edited reels ready to publish across your channels.",
        ],
      },
      {
        heading: "Content with a job to do",
        body: [
          "Reels are planned to support specific goals: brand awareness, package promotion, trust building and lead generation, so the content slots directly into your marketing instead of sitting unused.",
          "The footage works across Instagram, Facebook and Meta Ads, giving both your organic presence and your paid campaigns credible material.",
        ],
      },
      {
        heading: "Transparent about expenses",
        body: [
          "The production day is ₹30,000 one-time. Required locations, travel, production requirements and other agreed expenses are separate unless otherwise agreed.",
        ],
      },
    ],
    faqs: [
      {
        q: "What does the content day include?",
        a: "One professional content production day and 10 edited reels for ₹30,000 one-time. The reels are planned around your goals: brand awareness, package promotion, trust and lead generation.",
      },
      {
        q: "Are travel and location costs included?",
        a: "No. Required locations, travel, production requirements and other agreed expenses are separate unless otherwise agreed. We confirm the full scope before the day is booked.",
      },
      {
        q: "How is this different from the reels in monthly packages?",
        a: "Monthly packages include reels created from footage, information and media you supply. The content day is for agencies that want professionally captured footage instead.",
      },
    ],
    relatedArticles: ["meta-ads-hajj-umrah-agencies", "convert-enquiries-into-bookings", "hajj-lead-generation-india"],
  },
];

export const getServiceBySlug = (slug) =>
  SERVICE_PAGES.find((s) => s.slug === slug);
